/** @format */
import { RefObject, useRef, useState } from 'react';

import { useIsomorphicLayoutEffect } from '@toucan/client/global/nextjs/hooks/use-isomorphic-layout-effect';

export type ElementBreakpointsThreshold<T extends object> = {
    [key in keyof T]: number;
};

export type ElementBreakpointsStatus<T extends object> = {
    [key in keyof T]: boolean;
};

export enum ElementBreakpointsQuery {
    WIDTH = 'width',
    HEIGHT = 'height',
}

type UseElementBreakpointsQuery<T extends object> = {
    ref: RefObject<any> | null;
    breakpoints: ElementBreakpointsThreshold<T>;

    /**
     * The measurements to query:
     * WIDTH | HEIGHT
     *
     * @default WIDTH
     */
    query?: ElementBreakpointsQuery;
};

export const useElementBreakpoints = <T extends object>({
    ref,
    breakpoints,
    query = ElementBreakpointsQuery.WIDTH,
}: UseElementBreakpointsQuery<T>) => {
    const breakpointKeys = Object.keys(breakpoints);

    const defaultBreakpointsStatus = breakpointKeys.reduce(
        (breakpointsStatus, breakpoint) => ({
            ...breakpointsStatus,
            [breakpoint]: false,
        }),
        {} as ElementBreakpointsStatus<T>
    );

    // Allows for access to the most current breakpoints status without having to update state
    const breakpointsStatusRef = useRef({} as ElementBreakpointsStatus<T>);

    const [breakpointsStatus, setBreakpointsStatus] = useState(
        defaultBreakpointsStatus
    );

    const getBreakpointsStatus = (size: number) => {
        const newBreakpointsStatus = {} as ElementBreakpointsStatus<T>;
        let shouldUpdateBreakpointsStatus = false;

        for (const [key, value] of Object.entries<number>(breakpoints)) {
            const isSizeLargerThanBreakpoint = size > value;
            // @ts-ignore - Element implicitly has an any type - Touch and Change
            newBreakpointsStatus[key] = isSizeLargerThanBreakpoint;

            // only update the state if one of the breakpoints thresholds has changed
            if (
                // @ts-ignore - Element implicitly has an any type - Touch and Change
                breakpointsStatusRef?.current?.[key] !==
                isSizeLargerThanBreakpoint
            ) {
                shouldUpdateBreakpointsStatus = true;
            }
        }

        if (shouldUpdateBreakpointsStatus) {
            setBreakpointsStatus(newBreakpointsStatus);
            breakpointsStatusRef.current = newBreakpointsStatus;
        }
    };

    const getClientResizeObserver = () => {
        if (typeof window === 'undefined') {
            return undefined;
        }

        return new ResizeObserver(([entry]) => {
            if (!entry?.borderBoxSize?.[0]) {
                return;
            }

            const { inlineSize: width, blockSize: height } =
                entry.borderBoxSize[0];

            const axisSize =
                query === ElementBreakpointsQuery.WIDTH ? width : height;

            getBreakpointsStatus(axisSize);
        });
    };

    const resizeObserverRef = useRef(getClientResizeObserver());

    useIsomorphicLayoutEffect(() => {
        if (ref?.current) {
            resizeObserverRef.current?.observe?.(ref.current);
        }

        return () => resizeObserverRef.current?.disconnect?.();
    }, [ref]);

    return breakpointsStatus;
};
